import { b2AddVec2, b2CloneVec2, b2MulVec2 } from "../../../libs/debugdraw";
import {
    game
} from "../../Game";
import {
    Settings
} from '../../Settings';

const { getPointer, NULL } = Box2D;

const vec1 = new Box2D.b2Vec2();

// A grouped body can have several fixtures inside the camera sensor at once.
// The old boolean-only bookkeeping put the entire body to sleep as soon as the
// first fixture left the sensor, even when another fixture was still visible.
const overlappingFixturesByBody = new Map();
let pendingRebuildBodies = new Set();
let followedFocus = false;

const clearOverlapState = body => {
	overlappingFixturesByBody.delete(body);
	delete body.InCameraView;
}

const registerOverlap = (body, fixture) => {
	let fixtures = overlappingFixturesByBody.get(body);
	if(!fixtures){
		fixtures = new Set();
		overlappingFixturesByBody.set(body, fixtures);
	}
	fixtures.add(fixture);
	body.InCameraView = true;
	pendingRebuildBodies.delete(body);
}

const unregisterOverlap = (body, fixture) => {
	const fixtures = overlappingFixturesByBody.get(body);
	if(fixtures){
		fixtures.delete(fixture);
		// A destroyed shape does not always emit a usable sensor-end event.
		// Prune destroyed grouped fixtures when another event reaches the body.
		fixtures.forEach(overlapFixture => {
			if(overlapFixture.destroyed) fixtures.delete(overlapFixture);
		});
		if(fixtures.size) return false;
	}
	clearOverlapState(body);
	return true;
}

const connectedBodiesFor = rootBody => {
	const connectedBodies = [];
	const crawledBodies = new Set();
	const crawledJoints = new Set();

	const crawlJoints = body => {
		if(!body || crawledBodies.has(body)) return;
		crawledBodies.add(body);
		connectedBodies.push(body);

		for (let jointEdge = body.GetJointList(); getPointer(jointEdge) !== getPointer(NULL); jointEdge = jointEdge.get_next()) {
			const joint = jointEdge.joint;
			if(!joint || crawledJoints.has(joint)) continue;
			crawledJoints.add(joint);

			const bodyA = joint.GetBodyA();
			const bodyB = joint.GetBodyB();
			if(bodyA !== rootBody) bodyA.isVehiclePart = true;
			if(bodyB !== rootBody) bodyB.isVehiclePart = true;
			crawlJoints(bodyA);
			crawlJoints(bodyB);
		}
	}

	crawlJoints(rootBody);
	return connectedBodies;
}

const freezeConnectedBodies = otherBody => {
	if(!otherBody?.mySprite || otherBody.destroyed) return;
	if(!otherBody.mySprite.data.awake || !otherBody.IsAwake()) return;
	if(otherBody.GetType() === Box2D.b2_staticBody || otherBody.ignorePhysicsCuller) return;

	const connectedBodies = connectedBodiesFor(otherBody);
	// Sleeping a body can sleep its complete connected joint island. If any
	// connected body is visible or opted out, the whole island must remain awake.
	const canFreeze = connectedBodies.every(body => !body.InCameraView && !body.ignorePhysicsCuller);
	if(!canFreeze) return;

	connectedBodies.forEach(body => {
		if(!body.destroyed && body.GetType() !== Box2D.b2_staticBody) body.SetAwake(false);
	});
}

const reconcileRebuiltSensor = () => {
	if(!pendingRebuildBodies.size) return;
	const bodies = pendingRebuildBodies;
	pendingRebuildBodies = new Set();
	bodies.forEach(body => {
		// New sensor begin-events have already run during this frame's physics
		// step. Anything that did not re-enter is outside the resized camera.
		if(!body.InCameraView) freezeConnectedBodies(body);
	});
}

export const init = ()=>{
	followedFocus = false;
	overlappingFixturesByBody.forEach((_fixtures, body) => delete body.InCameraView);
	overlappingFixturesByBody.clear();
	pendingRebuildBodies.clear();

	const bd = new Box2D.b2BodyDef();
	bd.type = Box2D.b2_dynamicBody;
	game.editor.physicsCamera = game.editor.CreateBody(bd);
	game.editor.physicsCamera.SetSleepingAllowed(false);
	game.editor.physicsCamera.SetGravityScale(0);
	game.editor.physicsCamera.isPhysicsCamera = true;
	Box2D.destroy(bd);
}

export const update = ()=>{
	if(!game.editor.physicsCamera) return;
	reconcileRebuiltSensor();

	if(game.editor.physicsCamera.cameraZoom != game.editor.editorSettingsObject.cameraZoom){
		game.editor.physicsCamera.reFixture = true;

		// Replacing the camera fixture may not produce a useful EndContact after
		// the old camera fixture is destroyed. Reset the overlap
		// generation now and reconcile bodies after the new fixture has stepped.
		pendingRebuildBodies = new Set(overlappingFixturesByBody.keys());
		overlappingFixturesByBody.forEach((_fixtures, otherBody) => delete otherBody.InCameraView);
		overlappingFixturesByBody.clear();

		const body = game.editor.physicsCamera;

		let fixture;

		const fixturesToDestroy = [];
		for (fixture = body.GetFixtureList(); getPointer(fixture) !== getPointer(NULL); fixture = fixture.GetNext()) {
			fixturesToDestroy.push(fixture);
		}
		fixturesToDestroy.forEach(fixture => body.DestroyFixture(fixture));


		const fixDef = new Box2D.b2FixtureDef();

		const shape = new Box2D.b2CircleShape();
		const targetRadius = (Settings.targetResolution.x / 2) / Settings.PTM * game.editor.editorSettingsObject.physicsCameraSize;
		shape.set_m_radius(targetRadius / game.editor.editorSettingsObject.cameraZoom);

		fixDef.set_shape(shape);
		fixDef.set_isSensor(true);

		fixture = game.editor.physicsCamera.CreateFixture(fixDef);
		Box2D.destroy(shape);
		Box2D.destroy(fixDef);

		// also collide with only similar objects
		const filterData = fixture.GetFilterData();
		filterData.set_categoryBits(game.editor.MASKBIT_PHYSICS_CULL);
		filterData.set_maskBits(game.editor.MASKBIT_NORMAL | game.editor.MASKBIT_FIXED | game.editor.MASKBIT_CHARACTER | game.editor.MASKBIT_EVERYTHING_BUT_US | game.editor.MASKBIT_ONLY_US | game.editor.MASKBIT_NOTHING | game.editor.MASKBIT_NPC);
		fixture.SetFilterData(filterData);

		game.editor.physicsCamera.cameraZoom = game.editor.editorSettingsObject.cameraZoom;
		delete game.editor.physicsCamera.reFixture;
	}

	if(game.cameraFocusObject && !game.cameraFocusObject.destroyed){
		const targetPosition = vec1;
		targetPosition.Set(game.cameraFocusObject.GetPosition().x, game.cameraFocusObject.GetPosition().y);
		game.editor.physicsCamera.SetTransform(targetPosition, 0);
		followedFocus = true;
	}else if(!followedFocus){
		// The render camera depends on the screen size. Once a target was followed,
		// the sensor stays where it was last seen so the simulation does not.
		let cameraX = -(game.editor.cameraHolder.x / game.editor.cameraHolder.scale.x - (window.innerWidth / 2 / game.editor.cameraHolder.scale.x)) / Settings.PTM;
		let cameraY = -(game.editor.cameraHolder.y / game.editor.cameraHolder.scale.y - (window.innerHeight / 2 / game.editor.cameraHolder.scale.y)) / Settings.PTM;
		const targetPosition = new Box2D.b2Vec2(cameraX, cameraY);
		game.editor.physicsCamera.SetTransform(targetPosition, 0);
	}
}

export const beginContact = contact => {
	const otherFixture = contact.GetFixtureA().GetBody().isPhysicsCamera ? contact.GetFixtureB() : contact.GetFixtureA();
	const otherBody = otherFixture.GetBody();

	if(otherBody.mySprite){

		const isPrefab = otherBody.mySprite.data.prefabInstanceName;

		let allowAwake;
		if(!isPrefab) allowAwake = otherBody.mySprite.data.awake;
		else{
			const prefab = game.editor.activePrefabs[otherBody.mySprite.data.prefabInstanceName];
			allowAwake = (prefab.settings.isAwake === undefined || prefab.settings.isAwake);
		}

		if(allowAwake){
			otherBody.SetAwake(true);
			registerOverlap(otherBody, otherFixture);
		}
	}
}

export const endContact = contact => {
	const otherFixture = contact.GetFixtureA().GetBody().isPhysicsCamera ? contact.GetFixtureB() : contact.GetFixtureA();
	const otherBody = otherFixture.GetBody();

	if(!otherBody.mySprite || !game.editor.physicsCamera || game.editor.physicsCamera.reFixture) return;
	if(!unregisterOverlap(otherBody, otherFixture)) return;
	freezeConnectedBodies(otherBody);
}
